import { CreditCardFilled, BankFilled } from "@ant-design/icons";
import { Col, Grid, Row, Space, Typography } from "antd";
import { useStore } from "../../../context/CreateStoreContext";
import { pricing2025 } from "../../../constants/pricingTier";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import style from "./StoreDetails.module.css";

const { Text, Title, Paragraph, Link } = Typography;
const { useBreakpoint } = Grid;

export default function Subscription() {
  const screen = useBreakpoint();
  const { selectedStore } = useStore();
  const [plan, setPlan] = useState(null);
  const activeStatus = ["active", "trialing", "cancelling"];
  const currentStatus = selectedStore?.stripeSubscription?.subscriptionStatus;
  const isIntegrator = selectedStore?.integratorId || false;

  useEffect(() => {
    const plan = pricing2025?.[selectedStore?.stripeSubscription?.plan];
    setPlan(plan || null);
  }, [selectedStore]);

  const getSubscriptionLabel = () => {
    const statusLabels = {
      cancelling: "Only active until ",
      canceled: "Not subscribed since ",
      inactive: "Not subscribed ",
      default: "on ",
    };

    let displayDate = null;

    // Billing starts with the store's first delivery order, not on a date.
    if (currentStatus === "trialing") {
      return (
        <Text style={{ color: "#515151" }}>
          Starts with your <span style={{ fontWeight: "bold" }}>first delivery order</span>
        </Text>
      );
    }

    const label = statusLabels[currentStatus] || statusLabels.default;

    if (currentStatus === "cancelling" && selectedStore?.cancelAt) {
      displayDate = dayjs.unix(selectedStore.cancelAt).format("MMM D, YYYY");
    } else if (currentStatus === "canceled" && selectedStore?.endedAt) {
      displayDate = dayjs.unix(selectedStore.endedAt).format("MMM D, YYYY");
    } else if (selectedStore?.upcoming?.dueDate) {
      displayDate = dayjs
        .unix(selectedStore?.upcoming?.dueDate)
        .format("MMM D, YYYY");
    }

    return (
      <Text style={{ color: "#515151" }}>
        {label} <span style={{ fontWeight: "bold" }}>{displayDate}</span>
      </Text>
    );
  };

  const renderSubscriptionStatus = () => {
    if (activeStatus.includes(currentStatus)) {
      return (
        <Active
          plan={plan}
          status={currentStatus}
          hidePricing={isIntegrator}
          screen={screen}
        />
      );
    }
    if (["canceled", "inactive"].includes(currentStatus)) {
      return (
        <CanceledInactive plan={plan} status={currentStatus} screen={screen} />
      );
    }
    return <NoSubscription screen={screen} />;
  };

  return (
    <Row
      className={style.Subscription}
      style={{
        backgroundColor: activeStatus.includes(currentStatus)
          ? "rgb(0 122 255 / 5%)"
          : "#F8F8F8",
      }}
      gutter={[0, 20]}
    >
      <Col span={24}>
        <Row align="bottom">
          <Col xs={24} lg={14}>
            {renderSubscriptionStatus()}
          </Col>
          <Col xs={24} lg={10}>
            <Paragraph
              style={{
                fontSize: "12px",
                margin: 0,
                color: "#000000a6",
                textAlign:
                  screen.xs === true || screen.lg === false
                    ? "start"
                    : "sendtaendrt",
                marginTop:
                  screen.xs === true || screen.lg === false ? "20px" : "0",
              }}
            >
              For any subscription or billing concerns, please contact our
              Customer Support at{" "}
              <Link
                href="tel:8182351327"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                (818) 235-1327
              </Link>{" "}
              or{" "}
              <Link
                href="mailto:support@bevz.com"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                support@bevz.com
              </Link>
              .
            </Paragraph>
          </Col>
        </Row>
      </Col>

      {isIntegrator ? null : (
        <Col span={24}>
          <Row
            style={{
              padding: "20px",
              borderRadius: "8px",
              border: selectedStore?.payment?.isExpired
                ? "1px solid #ff3b30"
                : "1px solid #E5E5EA",
              backgroundColor: selectedStore?.payment?.isExpired
                ? "#FFF2F1"
                : "white",
            }}
          >
            <Col xs={24} md={12}>
              <Space size="10" direction="vertical">
                <Text strong style={{ color: "#3A3A3C" }}>
                  Payment Method
                </Text>
                <Space>
                  {selectedStore?.payment?.brand ? (
                    <PaymentType {...selectedStore?.payment} />
                  ) : (
                    <Text style={{ color: "#515151" }}>
                      No active payment method is currently set.
                    </Text>
                  )}
                </Space>
              </Space>
            </Col>

            <Col
              xs={24}
              md={12}
              style={{ textAlign: screen.xs ? "start" : "end" }}
            >
              <Space size="10" direction="vertical">
                <Text strong style={{ color: "#3A3A3C" }}>
                  {activeStatus.includes(currentStatus) &&
                  currentStatus !== "cancelling"
                    ? "Upcoming charges"
                    : "No upcoming charges"}
                </Text>
                {getSubscriptionLabel()}
              </Space>
            </Col>
          </Row>
          {selectedStore?.payment?.isExpired && (
            <div style={{ marginTop: "5px", color: "#F5222D" }}>
              There’s an issue with your payment method. Contact customer
              support for assistance.
            </div>
          )}
        </Col>
      )}
    </Row>
  );
}

const PaymentType = ({ type, isExpired, last4, brand }) => {
  return (
    <>
      {type === "card" ? (
        <CreditCardFilled
          style={{
            color: isExpired ? "#F5222D" : "#007AFF",
          }}
        />
      ) : type === "us_bank_account" ? (
        <BankFilled
          style={{
            color: isExpired ? "#F5222D" : "#007AFF",
          }}
        />
      ) : null}{" "}
      <Text style={{ color: "#515151" }}>
        <span style={{ textTransform: "capitalize" }}>{brand}</span>{" "}
        {type === "us_bank_account" ? "bank account" : null} ending in{" "}
        <span style={{ fontWeight: "bold" }}>{last4}</span>
      </Text>
    </>
  );
};

const Active = ({ plan, status, hidePricing, screen }) => {
  const displayStatus = hidePricing
    ? null
    : status === "cancelling"
    ? "Canceled"
    : plan?.price
    ? `$${plan?.price}/month`
    : null;

  return (
    <>
      <div className={`${style.Tag} ${style.active}`}>CURRENT PLAN</div>
      <Title
        className={style.Title}
        style={{
          fontSize:
            screen.xs || screen.lg === false
              ? "20px"
              : screen.lg === true
              ? "24px"
              : "20px",
        }}
      >
        {plan?.label} Plan
      </Title>
      <Text strong className={style.Subtitle}>
        {displayStatus}
      </Text>
    </>
  );
};

const CanceledInactive = ({ plan, status, screen }) => (
  <>
    <div className={`${style.Tag} ${style.inactive}`}>
      {status === "canceled" ? "NO ACTIVE PLAN" : "INACTIVE ACCOUNT"}
    </div>
    <Title
      className={style.Title}
      style={{
        fontSize:
          screen.xs || screen.lg === false
            ? "20px"
            : screen.lg === true
            ? "24px"
            : "20px",
      }}
    >
      No Subscription
    </Title>
    <Text strong className={style.Subtitle}>
      {status === "canceled" ? "Canceled" : "Inactive"} ({plan?.label} Plan)
    </Text>
  </>
);

const NoSubscription = ({ screen }) => (
  <>
    <div className={`${style.Tag} ${style.inactive}`}>INACTIVE ACCOUNT</div>
    <Title
      className={style.Title}
      style={{
        fontSize:
          screen.xs || screen.lg === false
            ? "20px"
            : screen.lg === true
            ? "24px"
            : "20px",
      }}
    >
      No Subscription
    </Title>
    <Text strong className={style.Subtitle}>
      {/* Inactive */}
    </Text>
  </>
);
